import { useEffect, useState } from "react";
import axios from "axios";
import { base_url } from "../context/Authcontext";

/* =====================================================================
   Banner feed
   =====================================================================
   One fetch of /api/webapi/getAllBanner shared by every strip on the
   page. The hero and the promo row both want banners, and two components
   each opening their own request for the same list is how the live-odds
   double-fetch happened.

   The server has already dropped anything inactive or outside its
   schedule window, so whatever arrives here is live now. This module only
   sorts it into placements and picks the right artwork for the device.
   ===================================================================== */

let cache = null;
let inFlight = null;
const listeners = new Set();

const publish = () => listeners.forEach((fn) => fn(cache));

const load = () => {
  if (inFlight) return inFlight;
  inFlight = axios
    .get(`${base_url}/api/webapi/getAllBanner`)
    .then((res) => {
      cache = res.data && res.data.success ? res.data.data || [] : [];
      return cache;
    })
    .catch((err) => {
      // The hero falls back to bundled artwork rather than showing an
      // error, so a failed banner fetch is not worth breaking the page
      // over -- but it should be visible in the console.
      console.error("Error fetching banners:", err);
      cache = [];
      return cache;
    })
    .finally(() => {
      inFlight = null;
      publish();
    });
  return inFlight;
};

/* Refetch on demand -- used after the admin saves, and by the interval
   below so a scheduled banner appears without anyone reloading. */
export const refreshBanners = () => {
  cache = null;
  return load();
};

const isPhone = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

/* `type` holds Web / Phone and has been on the admin form all along, but
   the API never returned the column so nothing could act on it. A banner
   with neither value set shows everywhere. */
const matchesDevice = (banner, phone) => {
  const type = String(banner.type || "").toLowerCase();
  if (type === "phone") return phone;
  if (type === "web") return !phone;
  return true;
};

export const useBanners = (placement) => {
  const [rows, setRows] = useState(cache);
  const [phone, setPhone] = useState(isPhone);

  useEffect(() => {
    const onChange = (next) => setRows(next ? next.slice() : next);
    listeners.add(onChange);
    if (cache === null) load();
    else setRows(cache.slice());

    // A banner whose window opens while somebody is sitting on the page
    // should appear on its own. Five minutes is far below any realistic
    // scheduling granularity and costs one small request.
    const poll = setInterval(refreshBanners, 5 * 60 * 1000);

    // Re-evaluate on a real breakpoint crossing rather than on every
    // pixel of a window drag.
    const mq = window.matchMedia("(max-width: 768px)");
    const onMq = (e) => setPhone(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onMq);
    else mq.addListener(onMq);

    return () => {
      listeners.delete(onChange);
      clearInterval(poll);
      if (mq.removeEventListener) mq.removeEventListener("change", onMq);
      else mq.removeListener(onMq);
    };
  }, []);

  const wanted = String(placement || "hero").toLowerCase();
  let banners = null; // still loading -- callers reserve space rather than collapse
  if (rows !== null) {
    const inPlacement = rows.filter(
      (b) => String(b.placement || "hero").toLowerCase() === wanted,
    );
    let chosen = inPlacement.filter((b) => matchesDevice(b, phone));
    // A phone with no Phone-type artwork gets the Web banners rather than
    // nothing (and the bundled fallbacks) -- the carousel shows wide art
    // uncropped on a phone, so web creatives stay legible (#5).
    if (phone && chosen.length === 0) {
      chosen = inPlacement;
    }
    banners = chosen.map((b) => ({
      id: b.id,
      title: b.title || "",
      src: base_url + b.attachment,
      link: b.link || null,
      // Web artwork is drawn wide; the carousel must not crop it to a
      // phone's 2:1 slot.
      wide: String(b.type || "").toLowerCase() !== "phone",
    }));
  }

  return banners;
};

export default useBanners;
